import * as configcat from 'configcat-js';
import type { FeatureFlags } from '@client/types';

// ConfigCat key for each flag. Typed as Record<keyof FeatureFlags, string> so
// adding a flag to FeatureFlags forces adding its key (and default) here — no
// extra getValueAsync call to wire up by hand.
const FLAG_KEYS: Record<keyof FeatureFlags, string> = {
  multiAccountEnabled: 'multi_account_enabled',
  statelessUrlAppend: 'growth_hub_stateless_url_append',
};

// Fail-safe defaults: multi-account off, never append links. Used until the
// flags resolve and whenever ConfigCat is unavailable (no SDK key, network
// error) so behavior only changes on an explicit flag.
const DEFAULT_FLAGS: FeatureFlags = {
  multiAccountEnabled: false,
  statelessUrlAppend: false,
};

const FLAG_NAMES = Object.keys(FLAG_KEYS) as (keyof FeatureFlags)[];

const CONFIG_CACHE_TTL_SECONDS = 120;

/**
 * Account/user attributes used for ConfigCat targeting rules (e.g. enable a
 * flag for one account or email). When omitted, flags evaluate without a User
 * object (percentage / global defaults only).
 */
export interface FeatureFlagUserContext {
  accountId: string | number;
  email: string;
}

/**
 * Evaluates the stateless-navigation feature flags directly against ConfigCat
 * from the browser (frontend SDK key), so no BFF endpoint is needed. The
 * ConfigCat SDK key is read-only and safe to embed in the client bundle.
 */
export class FeatureFlagService {
  private client: configcat.IConfigCatClient | null = null;

  constructor(sdkKey: string | undefined = process.env.CONFIGCAT_SDK_KEY) {
    if (!sdkKey) return;

    try {
      this.client = configcat.getClient(
        sdkKey,
        configcat.PollingMode.LazyLoad,
        {
          cacheTimeToLiveSeconds: CONFIG_CACHE_TTL_SECONDS,
          logger: configcat.createConsoleLogger(configcat.LogLevel.Error),
        },
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to instantiate ConfigCat client', e);
      this.client = null;
    }
  }

  /**
   * Build a ConfigCat User for account/email targeting. Identifier and Email
   * are the user email; `account_id` is a custom attribute (same shape as the
   * server-side ConfigCat helper).
   */
  private createUser(user: FeatureFlagUserContext): configcat.User {
    const email = user.email;
    const accountId = String(user.accountId);

    return new configcat.User(email, email, '', {
      account_id: accountId,
    });
  }

  async getFlags(user?: FeatureFlagUserContext): Promise<FeatureFlags> {
    const { client } = this;
    if (!client) return { ...DEFAULT_FLAGS };

    try {
      const flags: FeatureFlags = { ...DEFAULT_FLAGS };
      const configCatUser = user ? this.createUser(user) : undefined;

      await Promise.all(
        FLAG_NAMES.map(async (name) => {
          flags[name] = await client.getValueAsync(
            FLAG_KEYS[name],
            DEFAULT_FLAGS[name],
            configCatUser,
          );
        }),
      );

      return flags;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);

      return { ...DEFAULT_FLAGS };
    }
  }
}
